import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  CardTitle,
  Table,
  Badge,
  Modal,
  ModalHeader,
  ModalBody,
  Button
} from "reactstrap"; // Modal bileşenleri eklendi

// Pages Components
import WelcomeComp from "./WelcomeComp";

// Import Breadcrumb
import Breadcrumbs from "../../components/Common/Breadcrumb";

// i18n
import { withTranslation } from "react-i18next";

// Axios
import axios from "axios";

const Dashboard = props => {
  // Sayfa Başlığı
  document.title = "Anasayfa | Subsea Admin";

  // --- API Endpointleri ---
  // Not: API URL'lerinizin sonuna /api eklenmesi gerekiyorsa lütfen düzeltin (önceki örneklerde /api/Listings idi)
  const API_LISTINGS = "https://subseaapi.stechomeyazilim.info/Listings"; 
  const API_USERS = "https://subseaapi.stechomeyazilim.info/Users";
  const API_COMPANIES = "https://subseaapi.stechomeyazilim.info/Companies";
  const FILE_BASE_URL = "https://subseaapi.stechomeyazilim.info/files/";

  // --- State ---
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalListings: 0,
    totalCompanies: 0,
    pendingListings: 0
  });
  const [recentListings, setRecentListings] = useState([]);
  const [loading, setLoading] = useState(true);

  // --- Modal State ---
  const [modal, setModal] = useState(false);
  const [selectedListing, setSelectedListing] = useState(null);

  // --- Helper: Resim URL ---
  const getImageUrl = (url) => {
    if (!url) return null;
    if (url.startsWith("http")) return url; // Firebase veya dış link
    return FILE_BASE_URL + url; // Sunucu içi link
  };

  // --- Veri Çekme ---
  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        // Tüm istekleri paralel atıyoruz
        const [listingsRes, usersRes, companiesRes] = await Promise.all([
          axios.get(`${API_LISTINGS}?$expand=Categories,ListingImages`), // Resimleri ve Kategorileri alalım
          axios.get(API_USERS),
          axios.get(API_COMPANIES)
        ]);

        const listingsData = Array.isArray(listingsRes.data.value) ? listingsRes.data.value : listingsRes.data;
        const usersData = Array.isArray(usersRes.data.value) ? usersRes.data.value : usersRes.data;
        const companiesData = Array.isArray(companiesRes.data.value) ? companiesRes.data.value : companiesRes.data;

        // İstatistikleri Hesapla
        const pendingCount = listingsData.filter(l => l.Status === 'pending').length;

        setStats({
          totalListings: listingsData.length,
          totalUsers: usersData.length,
          totalCompanies: companiesData.length,
          pendingListings: pendingCount
        });

        // Son 5 İlanı Al (ID'ye göre tersten sırala)
        const sortedListings = listingsData.sort((a, b) => b.ID - a.ID).slice(0, 5);
        setRecentListings(sortedListings);

      } catch (error) {
        console.error("Dashboard veri hatası:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  // --- Modal Fonksiyonları ---
  const toggleModal = () => {
    setModal(!modal);
  };

  const handleViewClick = (listing) => {
    setSelectedListing(listing);
    setModal(true);
  };

  // --- Rapor Kartları ---
  const reports = [
    { 
      title: "Toplam Kullanıcı", 
      iconClass: "bx-user", 
      description: stats.totalUsers, 
      color: "primary" 
    },
    { 
      title: "Toplam Firma", 
      iconClass: "bx-building", 
      description: stats.totalCompanies, 
      color: "info" 
    },
    { 
      title: "Toplam İlan", 
      iconClass: "bx-list-ul", 
      description: stats.totalListings, 
      color: "success" 
    },
    { 
      title: "Onay Bekleyen", 
      iconClass: "bx-time-five", 
      description: stats.pendingListings, 
      color: "warning" 
    },
  ];

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs
            title={props.t("Yönetim")}
            breadcrumbItem={props.t("Anasayfa")}
          />

          <Row>
            {/* Sol taraf: Hoşgeldiniz Mesajı */}
            <Col xl="4">
              <WelcomeComp /> 
            </Col>

            {/* Sağ Taraf: İstatistik Kartları */}
            <Col xl="8">
              <Row>
                {reports.map((report, key) => (
                  <Col md="6" key={"_col_" + key}>
                    <Card className="mini-stats-wid">
                      <CardBody>
                        <div className="d-flex">
                          <div className="flex-grow-1">
                            <p className="text-muted fw-medium">
                              {props.t(report.title)}
                            </p>
                            <h4 className="mb-0">{loading ? "..." : report.description}</h4>
                          </div>
                          <div className={`avatar-sm rounded-circle bg-${report.color} align-self-center mini-stat-icon`}>
                            <span className={`avatar-title rounded-circle bg-${report.color}`}>
                              <i className={"bx " + report.iconClass + " font-size-24"}></i>
                            </span>
                          </div>
                        </div>
                      </CardBody>
                    </Card>
                  </Col>
                ))}
              </Row>
            </Col>
          </Row>

          {/* --- Son Eklenen İlanlar Tablosu --- */}
          <Row>
            <Col lg="12">
              <Card>
                <CardBody>
                  <CardTitle className="mb-4">Son Eklenen İlanlar</CardTitle>
                  <div className="table-responsive">
                    <Table className="table-nowrap table-hover mb-0 align-middle">
                      <thead className="table-light">
                        <tr>
                          <th>ID</th>
                          <th>Başlık</th>
                          <th>Kategori</th>
                          <th>Fiyat</th>
                          <th>Durum</th>
                          <th>Detay</th>
                        </tr>
                      </thead>
                      <tbody>
                        {loading ? (
                          <tr><td colSpan="6" className="text-center">Yükleniyor...</td></tr>
                        ) : recentListings.length === 0 ? (
                          <tr><td colSpan="6" className="text-center">Kayıt bulunamadı.</td></tr>
                        ) : (
                          recentListings.map((item) => (
                            <tr key={item.ID}>
                              <td><span className="fw-bold">#{item.ID}</span></td>
                              <td>{item.Title}</td>
                              <td>{item.Categories ? item.Categories.Name : "-"}</td>
                              <td>{item.PriceValue ? `${item.PriceValue} ${item.PriceCurrency}` : "Teklif"}</td>
                              <td>
                                <Badge 
                                  color={
                                    item.Status === 'active' ? 'success' : 
                                    item.Status === 'pending' ? 'warning' : 'danger'
                                  } 
                                  className="font-size-12"
                                >
                                  {item.Status.toUpperCase()}
                                </Badge>
                              </td>
                              <td>
                                {/* Modal Açan Buton */}
                                <Button
                                  color="primary"
                                  className="btn-sm btn-rounded waves-effect waves-light"
                                  onClick={() => handleViewClick(item)}
                                >
                                  Görüntüle
                                </Button>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </Table>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* --- DETAY MODALI --- */}
          <Modal isOpen={modal} toggle={toggleModal} size="lg" centered>
            <ModalHeader toggle={toggleModal}>
                İlan Detayı #{selectedListing?.ID}
            </ModalHeader>
            <ModalBody>
                {selectedListing ? (
                    <Row>
                        {/* Görsel Alanı */}
                        <Col md={5} className="text-center mb-3">
                            {selectedListing.ListingImages && selectedListing.ListingImages.length > 0 ? (
                                <img 
                                    src={getImageUrl(
                                        selectedListing.ListingImages.find(x=>x.IsCover)?.Url || selectedListing.ListingImages[0].Url
                                    )} 
                                    alt="Listing" 
                                    className="img-fluid rounded border"
                                    style={{ maxHeight: '300px', objectFit: 'contain' }}
                                />
                            ) : (
                                <div className="bg-light p-5 rounded text-muted border">
                                    <i className="bx bx-image-alt font-size-24 d-block mb-2"></i>
                                    Görsel Yok
                                </div>
                            )}
                            <div className="mt-3">
                                <Badge color={selectedListing.Type === 'request_service' ? 'info' : 'warning'} className="font-size-14 p-2">
                                    {selectedListing.Type === 'request_service' ? 'Service Request' : 'Equipment Request'}
                                </Badge>
                            </div>
                        </Col>

                        {/* Bilgi Alanı */}
                        <Col md={7}>
                            <h4 className="card-title mb-3">{selectedListing.Title}</h4>
                            <p className="text-muted">{selectedListing.Description}</p>
                            
                            <div className="table-responsive">
                                <Table className="table-sm table-borderless mb-0">
                                    <tbody>
                                        <tr>
                                            <th style={{width: '120px'}}>Kategori:</th>
                                            <td>{selectedListing.Categories?.Name || "-"}</td>
                                        </tr>
                                        <tr>
                                            <th>Fiyat:</th>
                                            <td className="fw-bold text-primary">
                                                {selectedListing.PriceValue ? `${selectedListing.PriceValue} ${selectedListing.PriceCurrency}` : "Teklif İsteyiniz"}
                                            </td>
                                        </tr>
                                        <tr>
                                            <th>Durum:</th>
                                            <td>{selectedListing.Condition || "-"}</td>
                                        </tr>
                                        <tr>
                                            <th>Satış Tipi:</th>
                                            <td>{selectedListing.SaleType || "-"}</td>
                                        </tr>
                                        <tr>
                                            <th>Konum:</th>
                                            <td>{selectedListing.LocationCity}, {selectedListing.LocationCountry}</td>
                                        </tr>
                                        <tr>
                                            <th>Firma ID:</th>
                                            <td>{selectedListing.CompanyID || "Bireysel"}</td>
                                        </tr>
                                        <tr>
                                            <th>Oluşturulma:</th>
                                            <td>{new Date(selectedListing.CreatedAt).toLocaleDateString()}</td>
                                        </tr>
                                    </tbody>
                                </Table>
                            </div>
                        </Col>
                    </Row>
                ) : (
                    <div className="text-center p-4">Yükleniyor...</div>
                )}
            </ModalBody>
          </Modal>

        </Container>
      </div>
    </React.Fragment>
  );
};

Dashboard.propTypes = {
  t: PropTypes.any,
  chartsData: PropTypes.any,
  onGetChartsData: PropTypes.func,
};

export default withTranslation()(Dashboard);